import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Download, Upload, ArrowRight, ShieldCheck, Loader2, AlertTriangle, Check,
} from "lucide-react";
import { API } from "@/lib/auth";

/**
 * Sync Content — export a JSON snapshot of every editable collection from one
 * environment, and import it into another. Never touches orders / users /
 * analytics — only content: homepage, shop products, courses, quiz, pamper
 * parties, blog, discount codes.
 */
export default function SyncManager() {
  const [busy, setBusy] = useState(null);   // "export" | "import"
  const [snapshot, setSnapshot] = useState(null);
  const [pending, setPending] = useState(null); // parsed file waiting for confirm
  const [prune, setPrune] = useState(false);
  const [lastSummary, setLastSummary] = useState(null);

  // ---------- Export ----------
  const doExport = async () => {
    setBusy("export");
    try {
      const r = await axios.get(`${API}/admin/sync/export`);
      const data = r.data;
      setSnapshot(data);
      // trigger download
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `malek-content-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Snapshot downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setBusy(null);
    }
  };

  // ---------- Import (file → confirm) ----------
  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        if (!parsed?.collections) throw new Error("Missing collections");
        setPending(parsed);
      } catch (err) {
        toast.error("Not a valid sync snapshot");
      }
    };
    reader.readAsText(file);
    // reset input value so the same file can be re-picked
    e.target.value = "";
  };

  const confirmImport = async () => {
    if (!pending) return;
    if (!window.confirm(
      "Import this snapshot into THIS environment? Content will be overwritten. " +
      "Orders, users and analytics are never touched."
    )) return;
    setBusy("import");
    try {
      const r = await axios.post(`${API}/admin/sync/import`, {
        version: pending.version || 1,
        collections: pending.collections,
        prune,
      });
      setLastSummary(r.data?.summary);
      setPending(null);
      toast.success("Content imported");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-sync-panel">
      <div>
        <div className="overline">Content sync</div>
        <p className="text-sm text-[hsl(var(--onyx)/0.6)] mt-1 max-w-2xl">
          Move your latest content between preview and production without losing hours re-typing.
          Export a snapshot from one environment, then import it into the other.
        </p>
      </div>

      {/* Never-touched notice */}
      <div className="rounded-2xl bg-blush/40 border border-[hsl(var(--gold)/0.4)] p-4 flex items-start gap-3">
        <ShieldCheck size={18} className="text-[hsl(var(--gold))] shrink-0 mt-0.5" />
        <div className="text-sm text-[hsl(var(--onyx)/0.75)] leading-relaxed">
          <strong>Safe to run any time.</strong> Sync only touches editable content
          (homepage, shop products, courses, quiz, pamper parties, blog, discount codes).
          Orders, customer accounts, payments and analytics are never modified.
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Export */}
        <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
          <div className="w-12 h-12 rounded-full bg-blush flex items-center justify-center">
            <Download size={18} className="text-[hsl(var(--burgundy))]" />
          </div>
          <h3 className="font-display text-2xl mt-4">Export snapshot</h3>
          <p className="text-sm text-[hsl(var(--onyx)/0.65)] mt-2 leading-relaxed">
            Download a JSON file containing all editable content from this environment.
            Then upload it in the other environment to sync.
          </p>
          <button
            onClick={doExport}
            disabled={busy === "export"}
            data-testid="sync-export-btn"
            className="mt-6 btn-pill btn-primary !text-xs disabled:opacity-50"
          >
            {busy === "export" ? <><Loader2 size={14} className="animate-spin" /> Exporting…</> : <><Download size={14} /> Download snapshot</>}
          </button>
          {snapshot && (
            <div className="mt-4 text-xs text-[hsl(var(--onyx)/0.55)]">
              Latest export: {Object.entries(snapshot.counts || {}).map(([k, v]) => `${v} ${k}`).join(" · ")}
            </div>
          )}
        </div>

        {/* Import */}
        <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6">
          <div className="w-12 h-12 rounded-full bg-blush flex items-center justify-center">
            <Upload size={18} className="text-[hsl(var(--burgundy))]" />
          </div>
          <h3 className="font-display text-2xl mt-4">Import snapshot</h3>
          <p className="text-sm text-[hsl(var(--onyx)/0.65)] mt-2 leading-relaxed">
            Upload a JSON snapshot from the other environment. You'll see a preview before it's applied.
          </p>
          <label className="mt-6 inline-block cursor-pointer btn-pill btn-outline !text-xs">
            <Upload size={14} /> Choose snapshot file
            <input
              type="file"
              accept="application/json,.json"
              onChange={onFile}
              data-testid="sync-import-file"
              className="hidden"
            />
          </label>
        </div>
      </div>

      {/* Import preview */}
      {pending && (
        <div className="rounded-3xl bg-white border border-[hsl(var(--onyx)/0.06)] p-6 space-y-4" data-testid="sync-import-preview">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--gold)/0.15)] flex items-center justify-center shrink-0">
              <AlertTriangle size={18} className="text-[hsl(var(--gold))]" />
            </div>
            <div>
              <div className="overline">Ready to import</div>
              <p className="text-sm text-[hsl(var(--onyx)/0.65)] mt-1">
                Snapshot exported {pending.exported_at ? new Date(pending.exported_at).toLocaleString() : "at an unknown time"}.
                Review below then click <strong>Apply</strong>.
              </p>
            </div>
          </div>

          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(pending.collections || {}).map(([name, docs]) => (
              <div key={name} className="flex items-center gap-3 p-3 rounded-2xl bg-blush/30">
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                  <ArrowRight size={12} className="text-[hsl(var(--burgundy))]" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[hsl(var(--onyx))] truncate">{name.replace(/_/g, " ")}</div>
                  <div className="text-xs text-[hsl(var(--onyx)/0.55)]">{docs.length} record{docs.length === 1 ? "" : "s"}</div>
                </div>
              </div>
            ))}
          </div>

          <label className="flex items-start gap-3 text-sm text-[hsl(var(--onyx)/0.75)] cursor-pointer">
            <input
              type="checkbox"
              checked={prune}
              onChange={(e) => setPrune(e.target.checked)}
              data-testid="sync-prune"
              className="mt-1 w-4 h-4 rounded border-[hsl(var(--onyx)/0.3)] text-[hsl(var(--burgundy))]"
            />
            <span>
              <strong>Delete local records not in the snapshot</strong> — leaves this environment identical to the source.
              Recommended when going preview → production.
            </span>
          </label>

          <div className="flex gap-3 flex-wrap">
            <button
              onClick={confirmImport}
              disabled={busy === "import"}
              data-testid="sync-apply"
              className="btn-pill btn-primary !text-xs disabled:opacity-50"
            >
              {busy === "import" ? <><Loader2 size={14} className="animate-spin" /> Applying…</> : <><Check size={14} /> Apply import</>}
            </button>
            <button
              onClick={() => setPending(null)}
              className="btn-pill btn-outline !text-xs"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Last import summary */}
      {lastSummary && (
        <div className="rounded-3xl bg-white border border-green-200 p-6" data-testid="sync-summary">
          <div className="flex items-center gap-2">
            <Check size={16} className="text-green-600" />
            <div className="overline text-green-700">Import complete</div>
          </div>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {Object.entries(lastSummary).map(([name, s]) => (
              <div key={name} className="text-sm p-3 rounded-2xl bg-green-50 border border-green-100">
                <div className="font-medium text-[hsl(var(--onyx))]">{name.replace(/_/g, " ")}</div>
                <div className="text-xs text-[hsl(var(--onyx)/0.6)] mt-1">
                  ↑ {s.upserted} upserted
                  {s.deleted > 0 && <> · ✕ {s.deleted} pruned</>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
